// Hypertrophy-Focused 4-Day Upper/Lower Split
// Structured for 3-4 sessions per week, prioritizes upper body growth
// Uses standard exercise names with alternatives to suit different gyms

export const upperLowerTemplate = {
  name: "Upper/Lower Hypertrophy Split",
  description: "Hypertrophy-focused upper/lower split for 3-4 days per week with A/B variations",
  
  exercises: {
    upperA: [
      {
        primary: 'Chest Press Machine (Flat)',
        alternatives: ['Dumbbell Bench Press', 'Push-Ups', 'Smith Machine Bench Press'],
        sets: 3,
        target: 'Chest (mid/lower), front delts, triceps'
      },
      {
        primary: 'Lat Pulldown (Neutral or Underhand Grip)',
        alternatives: ['Assisted Pull-Up', 'Cable Lat Pullover', 'Pull-Up'],
        sets: 3,
        target: 'Lats, biceps'
      },
      {
        primary: 'Seated Row (Neutral Grip)',
        alternatives: ['Chest-Supported Row', 'Barbell Row', 'Dumbbell Row'],
        sets: 3,
        target: 'Mid-back (rhomboids, traps), rear delts, biceps'
      },
      {
        primary: 'Cable Lateral Raise',
        alternatives: ['Dumbbell Lateral Raise', 'Lateral Raise Machine'],
        sets: 3,
        target: 'Side delts'
      },
      {
        primary: 'Overhead Cable Triceps Extension (Rope or EZ Bar)',
        alternatives: ['Dumbbell Overhead Extension', 'Skull Crushers', 'Triceps Extension Machine'],
        sets: 3,
        target: 'Triceps (long head)'
      },
      {
        primary: 'Cable Biceps Curl (Rope or Straight Bar)',
        alternatives: ['Dumbbell Curl', 'Preacher Curl', 'EZ Bar Curl'],
        sets: 3,
        target: 'Biceps brachii'
      },
      {
        primary: 'Pec Deck Machine',
        alternatives: ['Cable Chest Fly (Flat)', 'Dumbbell Fly', 'Standing Cable Press'],
        sets: 3,
        target: 'Chest (isolation)'
      }
    ],

    upperB: [
      {
        primary: 'Incline Dumbbell Press',
        alternatives: ['Incline Chest Press Machine', 'Smith Machine Incline Press'],
        sets: 3,
        target: 'Upper chest, front delts, triceps'
      },
      {
        primary: 'Close-Grip Seated Cable Row',
        alternatives: ['Barbell Row (Close Grip)', 'Neutral Grip Pull-Up', 'Dumbbell Row'],
        sets: 3,
        target: 'Lats, mid-back, biceps'
      },
      {
        primary: 'Shoulder Press Machine',
        alternatives: ['Dumbbell Shoulder Press', 'Smith Machine Overhead Press'],
        sets: 3,
        target: 'Delts (primarily front and side), triceps'
      },
      {
        primary: 'Cable Chest Fly (High to Low)',
        alternatives: ['Decline Chest Press', 'Dumbbell Decline Fly'],
        sets: 3,
        target: 'Lower chest'
      },
      {
        primary: 'Bayesian Cable Curl',
        alternatives: ['Incline Dumbbell Curl', 'Cable Curl with Arms Behind Body'],
        sets: 3,
        target: 'Biceps (long head stretch)'
      },
      {
        primary: 'Cable Triceps Pushdown (Straight Bar)',
        alternatives: ['Rope Pushdown', 'Dips (Assisted)', 'Triceps Kickbacks'],
        sets: 3,
        target: 'Triceps (lateral and long head)'
      },
      {
        primary: 'Rear Delt Cable Fly',
        alternatives: ['Reverse Pec Deck', 'Bent-Over Dumbbell Lateral Raise'],
        sets: 3,
        target: 'Rear delts, upper back'
      }
    ],
    
    lowerA: [
      {
        primary: 'Hack Squat',
        alternatives: ['Leg Press', 'Goblet Squat', 'Smith Machine Squat'],
        sets: 3,
        target: 'Quads (primary), glutes, adductors'
      },
      {
        primary: 'Romanian Deadlift (RDL) (Barbell or Dumbbells)',
        alternatives: ['Glute Ham Raise', 'Cable Pull-Through', 'Hip Hinge Machine'],
        sets: 3,
        target: 'Hamstrings, glutes'
      },
      {
        primary: 'Leg Extension',
        alternatives: ['Step-Ups', 'Sissy Squat Machine'],
        sets: 3,
        target: 'Quads (isolation)'
      },
      {
        primary: 'Lying Leg Curl Machine',
        alternatives: ['Seated Leg Curl', 'Stability Ball Leg Curl'],
        sets: 3,
        target: 'Hamstrings'
      },
      {
        primary: 'Standing Calf Raise',
        alternatives: ['Leg Press Calf Raise', 'Smith Machine Calf Raise'],
        sets: 3,
        target: 'Gastrocnemius'
      },
      {
        primary: 'Ab Crunch Machine',
        alternatives: ['Cable Crunch', 'Weighted Sit-Ups', 'Stability Ball Crunch'],
        sets: 3,
        target: 'Rectus abdominis'
      }
    ],

    lowerB: [
      {
        primary: 'Hip Thrust (Machine or Barbell)',
        alternatives: ['Glute Bridge', 'Cable Hip Thrust', 'Smith Machine Hip Thrust'],
        sets: 3,
        target: 'Glutes (primary), hamstrings'
      },
      {
        primary: 'Leg Press',
        alternatives: ['Hack Squat', 'Bulgarian Split Squat', 'Step-Up'],
        sets: 3,
        target: 'Quads (primary), glutes'
      },
      {
        primary: 'Seated Leg Curl Machine',
        alternatives: ['Lying Leg Curl', 'Nordic Curl', 'Stability Ball Leg Curl'],
        sets: 3,
        target: 'Hamstrings'
      },
      {
        primary: 'Cable Glute Kickback',
        alternatives: ['Side-Lying Hip Raise', 'Glute Med Machine', 'Clamshells'],
        sets: 3,
        target: 'Glute medius, glute max'
      },
      {
        primary: 'Seated Calf Raise',
        alternatives: ['Standing Calf Raise', 'Leg Press Calf Raise'],
        sets: 3,
        target: 'Soleus'
      },
      {
        primary: 'Face Pull (Cable)',
        alternatives: ['Reverse Pec Deck', 'Band Pull-Apart'],
        sets: 3,
        target: 'Rear delts, traps, rotator cuff'
      }
    ]
  }
};

// Helper function to get next workout suggestion
export function suggestNextWorkout(recentExerciseLogs: Array<{ exercise: string }> = []) {
  // Simple logic: look at yesterday's exercises
  if (!recentExerciseLogs.length) {
    return { 
      suggested: 'upper', 
      reason: 'Start with upper body' 
    };
  }

  // Check if recent exercises match upper or lower template (A or B variants)
  const upperExercises = [
    ...upperLowerTemplate.exercises.upperA.map(ex => ex.primary),
    ...upperLowerTemplate.exercises.upperB.map(ex => ex.primary)
  ];
  const lowerExercises = [
    ...upperLowerTemplate.exercises.lowerA.map(ex => ex.primary),
    ...upperLowerTemplate.exercises.lowerB.map(ex => ex.primary)
  ];
  
  const recentExerciseNames = recentExerciseLogs.map(log => log.exercise);
  
  const upperMatches = recentExerciseNames.filter(ex => upperExercises.includes(ex));
  const lowerMatches = recentExerciseNames.filter(ex => lowerExercises.includes(ex));
  
  if (upperMatches.length > lowerMatches.length) {
    return { 
      suggested: 'lower', 
      reason: 'You did upper body recently' 
    };
  } else if (lowerMatches.length > upperMatches.length) {
    return { 
      suggested: 'upper', 
      reason: 'You did lower body recently' 
    };
  }
  
  return { 
    suggested: 'upper', 
    reason: 'Upper body is a good start' 
  };
}